import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { stringify } from '../../util/urlHelpers';
import NamedLink from '../NamedLink/NamedLink';
import { ChevronLeftIcon13, ChevronRightIcon13 } from '../RacingThemeSearch/RacingThemeSearchIcons';
import { SEARCH_PAGINATION } from '../RacingThemeSearch/searchResultsContent';
import styles from './RacingThemeSearchPagination.module.css';

const getPageNumbersArray = (page, totalPages) => {
  const numbersFrom1ToTotalPages = Array.from({ length: totalPages }, (_, i) => i + 1);
  return numbersFrom1ToTotalPages
    .filter(v => v === 1 || Math.abs(v - page) <= 1 || v === totalPages)
    .reduce((newArray, p) => {
      const isFirstPageOrNextToCurrentPage = p === 1 || newArray[newArray.length - 1] + 1 === p;
      return isFirstPageOrNextToCurrentPage ? newArray.concat([p]) : newArray.concat(['\u2026', p]);
    }, []);
};

/**
 * Search results pagination — uses NamedLink when page routing props are provided.
 */
const RacingThemeSearchPagination = props => {
  const {
    currentPage = SEARCH_PAGINATION.currentPage,
    pages = SEARCH_PAGINATION.pages,
    trailingPage = SEARCH_PAGINATION.trailingPage,
    showEllipsis = SEARCH_PAGINATION.showEllipsis,
    pagination,
    pageName,
    pagePathParams,
    pageSearchParams,
  } = props;

  const useLinks = !!(pagination && pageName);

  if (!pagination) {
    return null;
  }

  if (useLinks) {
    const { page, totalPages, paginationLimit, paginationUnsupported } = pagination;
    const hasPaginationLimit = !!paginationLimit;
    const pageCountLimit = paginationUnsupported
      ? 1
      : hasPaginationLimit
      ? paginationLimit
      : totalPages;

    if (!pageCountLimit || pageCountLimit <= 1) {
      return null;
    }

    const prevPage = page > 1 ? page - 1 : null;
    const nextPage = page < pageCountLimit ? page + 1 : null;
    const pageItems = getPageNumbersArray(page, pageCountLimit);

    return (
      <nav className={styles.pagination} aria-label="Results pages">
        {prevPage ? (
          <NamedLink
            className={styles.pageBtn}
            name={pageName}
            params={pagePathParams}
            to={{ search: stringify({ ...pageSearchParams, page: prevPage }) }}
            ariaLabel="Previous page"
          >
            <ChevronLeftIcon13 />
          </NamedLink>
        ) : (
          <button type="button" className={styles.pageBtn} disabled aria-label="Previous page">
            <ChevronLeftIcon13 />
          </button>
        )}
        {pageItems.map((item, index) =>
          typeof item === 'number' ? (
            <NamedLink
              key={item}
              className={classNames(styles.pageBtn, page === item && styles.pageBtnActive)}
              name={pageName}
              params={pagePathParams}
              to={{ search: stringify({ ...pageSearchParams, page: item }) }}
              ariaLabel={`Page ${item}`}
            >
              {item}
            </NamedLink>
          ) : (
            <span key={`gap-${index}`} className={styles.pageEllipsis}>
              {item}
            </span>
          )
        )}
        {nextPage ? (
          <NamedLink
            className={styles.pageBtn}
            name={pageName}
            params={pagePathParams}
            to={{ search: stringify({ ...pageSearchParams, page: nextPage }) }}
            ariaLabel="Next page"
          >
            <ChevronRightIcon13 />
          </NamedLink>
        ) : (
          <button type="button" className={styles.pageBtn} disabled aria-label="Next page">
            <ChevronRightIcon13 />
          </button>
        )}
      </nav>
    );
  }

  return (
    <nav className={styles.pagination} aria-label="Results pages">
      <button type="button" className={styles.pageBtn} disabled aria-label="Previous page">
        <ChevronLeftIcon13 />
      </button>
      {pages.map(page => (
        <button
          key={page}
          type="button"
          className={classNames(styles.pageBtn, currentPage === page && styles.pageBtnActive)}
        >
          {page}
        </button>
      ))}
      {showEllipsis ? <span className={styles.pageEllipsis}>…</span> : null}
      <button
        type="button"
        className={classNames(styles.pageBtn, currentPage === trailingPage && styles.pageBtnActive)}
      >
        {trailingPage}
      </button>
      <button type="button" className={styles.pageBtn} aria-label="Next page">
        <ChevronRightIcon13 />
      </button>
    </nav>
  );
};

RacingThemeSearchPagination.propTypes = {
  currentPage: PropTypes.number,
  pages: PropTypes.arrayOf(PropTypes.number),
  trailingPage: PropTypes.number,
  showEllipsis: PropTypes.bool,
  pagination: PropTypes.object,
  pageName: PropTypes.string,
  pagePathParams: PropTypes.object,
  pageSearchParams: PropTypes.object,
};

export default RacingThemeSearchPagination;
